import React, { useCallback, useEffect, useState } from 'react';
import {
    Card, Tabs, Table, Tag, Space, Select, Input, Button, Form, Switch, InputNumber, Collapse, Tooltip, Typography, message,
} from 'antd';
import { ReloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import abuseService from '../../services/abuseService';
import UserDetailDrawer from '../UserDetailDrawer';

const { Text } = Typography;

const actionColor = { block: 'red', challenge: 'orange', log: 'blue' };

const Hits = () => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState({ days: 30 });
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0, by_action: {} });
    const [loading, setLoading] = useState(false);
    const [drawerUser, setDrawerUser] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await abuseService.getHits({ ...filters, page, limit: 20 });
            if (r?.success) setData(r.data);
        } catch (e) {
            message.error(e?.error || t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [filters, page, t]);

    useEffect(() => { load(); }, [load]);

    const set = (k) => (v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };

    const columns = [
        { title: t('Time'), dataIndex: 'created_at', width: 160 },
        { title: t('Rule'), dataIndex: 'rule', width: 140, render: (v) => <Tag>{v}</Tag> },
        { title: t('Action'), dataIndex: 'action', width: 90, render: (v) => <Tag color={actionColor[v]}>{v}</Tag> },
        { title: t('Kind'), dataIndex: 'kind', width: 130 },
        {
            title: t('User'), key: 'user',
            render: (_, r) => (r.user_id ? (
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setDrawerUser({ id: r.user_id, username: `#${r.user_id}`, email: r.email })}>
                    #{r.user_id} {r.email}
                </Button>
            ) : <Text type="secondary">{r.email || '-'}</Text>),
        },
        {
            title: t('Network'), key: 'net',
            render: (_, r) => (
                <Space size={4} wrap>
                    <Text style={{ fontSize: 12 }}>{r.ip || '-'}</Text>
                    {r.asn && <Text type="secondary" style={{ fontSize: 12 }}>AS{r.asn} {r.asn_org}</Text>}
                    {!!r.is_datacenter && <Tag color="orange">{t('Datacenter/VPN')}</Tag>}
                </Space>
            ),
        },
        {
            title: t('Device'), dataIndex: 'device_id', width: 120,
            render: (v) => (v ? <Text code>{v.slice(0, 12)}</Text> : '-'),
        },
        {
            title: t('Details'), dataIndex: 'detail', width: 90,
            render: (v) => (v ? <Tooltip title={JSON.stringify(v)}><QuestionCircleOutlined /></Tooltip> : null),
        },
    ];

    const opt = (vals) => vals.map((v) => ({ value: v, label: v }));

    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Select allowClear placeholder={t('Rule')} style={{ width: 170 }} onChange={set('rule')}
                    options={opt(['disposable_email', 'device_reuse', 'ip_prefix_limit', 'datacenter'])} />
                <Select allowClear placeholder={t('Action')} style={{ width: 120 }} onChange={set('action')}
                    options={opt(['block', 'challenge', 'log'])} />
                <Select allowClear placeholder={t('Category')} style={{ width: 130 }} onChange={set('category')}
                    options={opt(['register', 'coupon', 'trial', 'free_plan'])} />
                <Select value={filters.days} style={{ width: 120 }} onChange={set('days')}
                    options={[1, 7, 30, 90, 180].map((d) => ({ value: d, label: t('{{count}} days', { count: d }) }))} />
                <Input.Search allowClear placeholder={t('Email, IP or device')} style={{ width: 220 }} onSearch={set('q')} />
                <Button icon={<ReloadOutlined />} onClick={load} />
                {Object.entries(data.by_action || {}).map(([k, n]) => <Tag key={k} color={actionColor[k]}>{k}: {n}</Tag>)}
            </Space>
            <Table
                size="small"
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={data.items}
                scroll={{ x: 1000 }}
                pagination={{ current: page, pageSize: 20, total: data.total, onChange: setPage, showSizeChanger: false }}
            />
            <UserDetailDrawer visible={!!drawerUser} user={drawerUser} onClose={() => setDrawerUser(null)} onChanged={load} />
        </>
    );
};

const Settings = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [info, setInfo] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await abuseService.getSettings();
            if (r?.success) {
                form.setFieldsValue(r.data.settings);
                setInfo(r.data);
            }
        } catch (e) {
            message.error(e?.error || t('Failed to load'));
        }
    }, [form, t]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        const values = await form.validateFields();
        setSaving(true);
        try {
            const r = await abuseService.saveSettings(values);
            if (r?.success) message.success(t('Saved'));
        } catch (e) {
            message.error(e?.error || t('Failed to save'));
        } finally {
            setSaving(false);
        }
    };

    const modes = [
        { value: 'off', label: t('Off') },
        { value: 'log', label: t('Log only') },
        { value: 'block', label: t('Block') },
    ];
    const dcModes = [
        { value: 'off', label: t('Off') },
        { value: 'log', label: t('Log only') },
        { value: 'challenge', label: t('Captcha + verified email') },
    ];
    const tip = (text) => <Tooltip title={text}><QuestionCircleOutlined style={{ marginLeft: 4 }} /></Tooltip>;

    return (
        <Form form={form} layout="vertical" style={{ maxWidth: 640 }}>
            <Form.Item name="disposable_block" valuePropName="checked"
                label={<>{t('Block disposable emails at sign-up')}{tip(t('{{count}} domains in the bundled list', { count: info?.disposable_list_size || 0 }))}</>}>
                <Switch />
            </Form.Item>
            <Space wrap size="large">
                <Form.Item name="coupon_mode" label={<>{t('Coupons')}{tip(t('One per device, limited per IP prefix'))}</>}>
                    <Select options={modes} style={{ width: 160 }} />
                </Form.Item>
                <Form.Item name="trial_mode" label={t('Free trials')}>
                    <Select options={modes} style={{ width: 160 }} />
                </Form.Item>
                <Form.Item name="free_plan_mode" label={t('Free plans')}>
                    <Select options={modes} style={{ width: 160 }} />
                </Form.Item>
            </Space>
            <Space wrap size="large">
                <Form.Item name="ip_prefix_limit" label={<>{t('Accounts per IP prefix')}{tip(t('Other accounts per /24 (IPv6 /48) that may claim the same offer'))}</>}>
                    <InputNumber min={1} max={1000} style={{ width: 160 }} />
                </Form.Item>
                <Form.Item name="ip_window_days" label={t('Window (days)')}>
                    <InputNumber min={1} max={365} style={{ width: 160 }} />
                </Form.Item>
            </Space>
            <Space wrap size="large">
                <Form.Item name="dc_coupon_mode" label={<>{t('Datacenter/VPN: coupons and trials')}{tip(t('IP from a hosting, VPN or proxy network'))}</>}>
                    <Select options={dcModes} style={{ width: 220 }} />
                </Form.Item>
                <Form.Item name="dc_free_plan_mode" label={t('Datacenter/VPN: free plans')}>
                    <Select options={dcModes.slice(0, 2)} style={{ width: 160 }} />
                </Form.Item>
            </Space>
            <Space wrap size="large">
                <Form.Item name="alerts" valuePropName="checked" label={<>{t('Telegram alerts')}{tip(t('At most one per device per hour'))}</>}>
                    <Switch />
                </Form.Item>
                <Form.Item name="retention_days" label={t('Keep device records (days)')}>
                    <InputNumber min={7} max={3650} style={{ width: 160 }} />
                </Form.Item>
            </Space>
            <Collapse ghost style={{ marginBottom: 16 }} items={[{
                key: 'adv',
                label: t('Advanced'),
                children: (
                    <>
                        <Form.Item name="disposable_extra" label={t('Extra disposable domains')}>
                            <Input.TextArea rows={3} placeholder="example-temp.com" />
                        </Form.Item>
                        <Form.Item name="disposable_allow" label={t('Never treat as disposable')}>
                            <Input.TextArea rows={2} />
                        </Form.Item>
                        <Form.Item name="dc_keywords" label={t('Datacenter ASN name keywords')}>
                            <Input.TextArea rows={4} />
                        </Form.Item>
                        <Form.Item name="proxy_asns" label={t('Datacenter/VPN ASNs')}>
                            <Input.TextArea rows={3} />
                        </Form.Item>
                        <Form.Item name="residential_asns" label={t('Never flag these ASNs')}>
                            <Input.TextArea rows={2} />
                        </Form.Item>
                        <Form.Item name="tls_header" label={t('TLS fingerprint header')}>
                            <Input style={{ width: 240 }} />
                        </Form.Item>
                        <Form.Item name="asn_db_path" label={t('ASN database path')}
                            extra={info?.asn_db?.path ? `${info.asn_db.path} · ${info.asn_db.type || ''} ${info.asn_db.built || ''}` : t('No ASN database found')}>
                            <Input placeholder={t('Default')} />
                        </Form.Item>
                    </>
                ),
            }]} />
            <Button type="primary" onClick={save} loading={saving}>{t('Save')}</Button>
        </Form>
    );
};

const AbuseProtection = () => {
    const { t } = useTranslation();
    return (
        <Card title={t('Abuse protection')}>
            <Tabs items={[
                { key: 'hits', label: t('Rule hits'), children: <Hits /> },
                { key: 'settings', label: t('Settings'), children: <Settings /> },
            ]} />
        </Card>
    );
};

export default AbuseProtection;
